// Colors for project codes (Chris, 2026-10-30): grays for non-billable codes, a hue of its own for each
// billable project. Kept apart from codes.ts so it has no API dependency.

/** A code's color: a light fill and a darker edge of the same hue. */
export interface CodeColor {
  fill: string;
  edge: string;
}

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

/**
 * One color per code across the app (Chris, 2026-10-30). Non-billable codes (3S001–3S003, time off, a
 * project marked non-billable) each take a shade of gray; billable projects each take a hue of their
 * own, spread evenly round the wheel and stepped so codes next to each other in order sit far apart.
 * Past 16 billable codes a second, deeper tier offsets the hues so no two ever match.
 */
export function codeColors(codes: readonly { code: string; billable?: boolean }[]): Map<string, CodeColor> {
  const out = new Map<string, CodeColor>();
  const sorted = [...codes].sort((a, b) => a.code.localeCompare(b.code));
  const grays = sorted.filter((c) => !c.billable).map((c) => c.code);
  grays.forEach((code, i) => {
    const t = grays.length > 1 ? i / (grays.length - 1) : 0.5;
    out.set(code, { fill: `hsl(215 6% ${Math.round(94 - t * 18)}%)`, edge: `hsl(215 6% ${Math.round(58 - t * 28)}%)` });
  });
  const billable = sorted.filter((c) => c.billable).map((c) => c.code);
  const tiers = Math.max(1, Math.ceil(billable.length / 16));
  const slots = Math.max(1, Math.ceil(billable.length / tiers));
  let step = Math.max(1, Math.round(slots * 0.38));
  while (gcd(step, slots) !== 1) step++;
  billable.forEach((code, i) => {
    const tier = Math.floor(i / slots);
    const hue = Math.round((((i % slots) * step) % slots) * (360 / slots) + tier * (360 / slots / tiers)) % 360;
    out.set(code, { fill: `hsl(${hue} 65% ${88 - (tier % 3) * 8}%)`, edge: `hsl(${hue} 50% ${38 - (tier % 3) * 6}%)` });
  });
  return out;
}

/** A code the list doesn't have: gray when it's internal, else a hue from its letters. */
export function fallbackColor(code: string): CodeColor {
  if (/^3S/i.test(code)) return { fill: "hsl(215 6% 86%)", edge: "hsl(215 6% 45%)" };
  let hash = 0;
  for (const ch of code) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const hue = (hash * 137) % 360;
  return { fill: `hsl(${hue} 65% 88%)`, edge: `hsl(${hue} 50% 38%)` };
}
